import Client from 'fhir-kit-client';
import { Bundle, BundleEntry, Device, DocumentReference, FhirResource, Organization, Patient, Practitioner, RelatedPerson } from "fhir/r4";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Form, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Title } from "@fyrstain/fhir-front-library";
import DataTable, { DataTableColumn } from "src/components/DataTable/DataTable";

interface NoteRow {
    id: string;
    creationDate: string;
    subject: string;
    type: string | undefined;
    authors: string[];
    status: string;
}

const isBoardDocument = (documentReference: DocumentReference): boolean =>
    documentReference.type?.coding?.some(
        ({ code }) => code?.toLowerCase() === "board",
    ) ?? false;

const NoteList: FunctionComponent<{ baseParameters?: {} }> = (props) => {
    const { t } = useTranslation();
    /////////////////////////////////////
    //        Search Parameters        //
    /////////////////////////////////////

    const [authorName, setAuthorName] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    /////////////////////////////////////
    //              Table              //
    /////////////////////////////////////

    const columns: DataTableColumn<NoteRow>[] = [
        { text: i18n.t('label.creationdate'), dataField: "creationDate" },
        {
            text: t("forms.careTeam.subject"),
            dataField: "subject"
        },
        {
            text: t("label.type"),
            dataField: "type",
            headerStyle: () => {
                return { width: '13%', };
            }
        },
        {
            text: i18n.t('label.author'),
            dataField: "authors",
            formatter: (cell: string[]) => {
                return <div>
                    {cell.map(c => {
                        return <><span>{c}</span><br /></>;
                    })}
                </div>;
            },
        },
        {
            text: t("label.status"),
            dataField: "status",
            formatter: (cell: string, row: { id: string }) => {
                return <Form.Select
                    className="form-control"
                    name="status"
                    defaultValue={cell}
                    onChange={e => changeStatus(row.id, e.target.value)}
                >
                    {(["current", "superseded", "entered-in-error"] as const).map((status) => <option key={status} value={status}>{t(`forms.note.statusOptions.${status}`)}</option>)}
                </Form.Select>;
            },
            headerStyle: () => {
                return { width: '18%', };
            }
        }];
    const [data, setData] = useState<NoteRow[]>([]);

    /////////////////////////////////////
    //             Client              //
    /////////////////////////////////////

    const fhirClient = new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL as string,
    });

    /////////////////////////////////////
    //             Actions             //
    /////////////////////////////////////

    const navigate = useNavigate();

    /**
     * Search DocumentReference from the back to populate the table.
     * 
     * @returns     the Promise of the List of DocumentReference.
     */
    async function searchTestPlans(): Promise<NoteRow[]> {
        return searchTestPlansWithParams(startDate, endDate, authorName);
    }

    /**
     * Search DocumentReference from the back to populate the table.
     * 
     * @returns     the Promise of the List of DocumentReference.
     */
    async function searchTestPlansWithParams(startDate: string | null, endDate: string | null, authorName: string | null): Promise<NoteRow[]> {
        try {
            const response = await fhirClient.search({
                resourceType: 'DocumentReference',
                searchParams: buildSearchParameters(authorName, startDate, endDate)
            },
            );
            if (response.resourceType !== 'Bundle') {
                throw Error(response.statusText);
            }
            const bundle: Bundle = response as Bundle;
            return bundle.entry?.filter(
                entry => 'DocumentReference' === entry.resource?.resourceType
            ).map(
                entry => entry.resource as DocumentReference
            ).filter(
                documentReference => !isBoardDocument(documentReference),
            ).map(
                documentReference => {
                    return {
                        "id": documentReference.id ?? "",
                        "subject": getSubject(documentReference, bundle.entry ?? []),
                        "type": documentReference.type?.coding?.find(coding => coding.display) ?
                            documentReference.type?.coding?.find(coding => coding.display)?.display
                            : documentReference.type?.coding?.find(coding => coding.code)?.code,
                        "authors": getAuthors(documentReference, bundle.entry ?? []),
                        "creationDate": documentReference.date ? documentReference.date : "N/A",
                        "status": documentReference.status
                    }
                }
            ) ?? [];
        } catch (error) {
            console.log(error);
            onError();
        }
        return [];
    }

    function getSubject(documentReference: DocumentReference, entries: BundleEntry<FhirResource>[]) {
        const subjectId = documentReference.subject?.reference?.split('/')[1];
        const subjectResource = entries.find(entry => subjectId === entry.resource?.id)?.resource;
        if (subjectResource && 'Patient' === subjectResource.resourceType) {
            const subjectName = (subjectResource as Patient).name?.find(name => name);
            return subjectName?.given?.join(", ") + " " + subjectName?.family;
        }
        return "N/A";
    }

    function getAuthors(documentReference: DocumentReference, entries: BundleEntry<FhirResource>[]) {
        const result = [] as string[];
        documentReference.author?.forEach(author => {
            const authorId = author.reference?.split('/')[1];

            const authorResource = entries.find(entry => authorId === entry.resource?.id)?.resource;
            if (authorResource) {
                switch (authorResource.resourceType) {
                    case 'Practitioner':
                        result.push(getPractitionerLabel(authorResource));
                        break;
                    case 'Organization':
                        result.push(getOrganizationLabel(authorResource));
                        break;
                    case 'RelatedPerson':
                        result.push(getRelatedPersonLabel(authorResource));
                        break;
                    case 'Device':
                        result.push(getDeviceLabel(authorResource));
                        break;
                }
            }
        })
        return result;
    }

    function getPractitionerLabel(practitioner: Practitioner) {
        const practitionerName = practitioner.name?.find(name => name);
        return "(P) " + practitionerName?.given?.join(", ") + " " + practitionerName?.family;
    }

    function getOrganizationLabel(organization: Organization) {
        return "(O) " + organization.name;
    }

    function getRelatedPersonLabel(relatedPerson: RelatedPerson) {
        const relatedPersonName = relatedPerson.name?.find(name => name);
        return "(RP) " + relatedPersonName?.given?.join(", ") + " " + relatedPersonName?.family;
    }

    function getDeviceLabel(device: Device) {
        const deviceName = device.deviceName?.find(name => 'user-friendly-name' === name.type);
        return "(D) " + deviceName?.name;
    }

    /**
     * Build the SearchParameters for the Search used to populate the table.
     * 
     * @param authorName    the ID to search.
     * @param startDate  the Name to search.
     * @returns     the SearchParameters as a JSON object.
     */
    function buildSearchParameters(authorName: string | null | undefined, startDate: string | null | undefined, endDate: string | null | undefined): {} {
        var searchParams: {} = { "_elements": "id,author,date,type,status,subject", "_sort": "-date", "_include": "DocumentReference:*" };

        if (props.baseParameters) {
            searchParams = {
                ...props.baseParameters,
                ...searchParams
            }
        }

        // TODO Create title search param for TestPlan
        if (authorName && authorName.length > 0) {
            searchParams = {
                "author:Practitioner.family": authorName,
                ...searchParams
            }
        }
        if (startDate && startDate.length > 0 && !endDate) {
            searchParams = {
                "date": `ge${startDate}`,
                ...searchParams
            }
        }
        if (endDate && endDate.length > 0 && !startDate) {
            searchParams = {
                "date": `le${endDate}`,
                ...searchParams
            }
        }
        if (startDate && startDate.length > 0 && endDate && endDate.length > 0) {
            searchParams = {
                "date": [`le${endDate}`, `ge${startDate}`],
                ...searchParams
            }
        }
        return searchParams;
    }

    /**
     * Update the state with the updated Search result when the Search button is pressed.
     */
    async function onSearch() {
        setData(await searchTestPlans());
    }

    /**
     * Reset the values of the Search Criteria fields and the Search Results.
     */
    async function onReset() {
        setStartDate("");
        setEndDate("");
        setAuthorName("");
        setData(await searchTestPlansWithParams(null, null, null));
    }

    /**
    * Perform a PATCH on the server to update the status
    * 
    * @param id        the id of the resource.
    * @param status    the new status.
    */
    async function changeStatus(id: string, status: string) {
        try {
            const docRef = await fhirClient.read({
                resourceType: 'DocumentReference',
                id: id,
            }) as DocumentReference;

            docRef.status = status as DocumentReference["status"];

            const transactionBundle = { "resourceType": "Bundle" } as Bundle;
            transactionBundle.type = "transaction";
            transactionBundle.entry = [];
            const noteUuid = "urn:uuid:e478aedd-0cb1-4b50-8957-5201a3f891fa"
            transactionBundle.entry.push({
                "fullUrl": noteUuid,
                "resource": docRef,
                "request": {
                    "method": "PUT",
                    "url": "/DocumentReference"
                }
            } as BundleEntry);

            await fhirClient.transaction({
                body: transactionBundle as FhirResource & { type: "transaction" }
            });

        } catch (error) {
            console.log(error);
            onError();
        }
    }

    const onError = useCallback((error?: unknown) => {
        navigate("/Error", { state: { error } });
    }, [navigate]);

    const initialSearchRef = useRef(onSearch);
    initialSearchRef.current = onSearch;

    useEffect(() => {
        initialSearchRef.current();
    }, []);

    return <>
        <div className="section">
            <Card>
                <Card.Header>
                    <Title level={2} content={i18n.t('title.searchcriteria')} />
                </Card.Header>
                <Card.Body className="cardBody">
                    <div className="mediumContainer">
                        <Form.Label>{i18n.t('label.practitionername')} :</Form.Label>
                        <InputGroup className="mb-3">
                            <Form.Control
                                placeholder=""
                                aria-label="authorName"
                                aria-describedby="authorName"
                                value={authorName}
                                onChange={(event) => setAuthorName(event.target.value)}
                            />
                        </InputGroup>
                    </div>
                    <Title level={3} content={i18n.t('title.notecreationdate')} />
                    <div className="spaceBetweenContainer">
                        <div className="mediumContainer">
                            <Form.Label>{i18n.t('label.startdate')} : </Form.Label>
                            <InputGroup className="mb-3">
                                <Form.Control
                                    placeholder=""
                                    aria-label="startDate"
                                    aria-describedby="startDate"
                                    type="date"
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                />
                            </InputGroup>
                        </div>
                        <div className="mediumContainer">
                            <Form.Label>{i18n.t('label.enddate')} : </Form.Label>
                            <InputGroup className="mb-3">
                                <Form.Control
                                    placeholder=""
                                    aria-label="endDate"
                                    aria-describedby="endDate"
                                    type="date"
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                />
                            </InputGroup>
                        </div>
                    </div>
                    <div className="formActions">
                        <Button
                            className="button"
                            variant="danger"
                            onClick={onSearch}
                        >
                            {i18n.t('button.search')}
                        </Button>
                        <Button
                            className="button"
                            variant="secondary"
                            onClick={onReset}
                        >
                            {i18n.t('button.reset')}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
        <div className="section">
            <DataTable
                keyField="id"
                data={data}
                columns={columns}
            />
        </div>
    </>
};

export default NoteList;
